import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import SectionHeading from "@/components/SectionHeading";
import ContactForm from "@/components/ContactForm";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, Shield, BarChart3, Award, Star, ArrowRight, UserCheck, Store, Landmark, HeartPulse, Building2, Factory, Briefcase, Headset, Check, Truck, HardHat } from "lucide-react";
import cameleonMetiers from "@/assets/cameleon-metiers.png";
import heroImage from "@/assets/hero-employe-incognito.png";
import sectorRetail from "@/assets/sectors/retail.jpg";
import sectorIndustrie from "@/assets/sectors/industrie.jpg";
import sectorServices from "@/assets/sectors/services.jpg";
import sectorLogistique from "@/assets/sectors/logistique.jpg";
import videoPoster from "@/assets/video/cameleon-hero-poster.jpg";
import { useLocale, useTranslation } from "@/i18n";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.7 },
};

const serviceIcons = [
  <Eye size={28} />,
  <Star size={28} />,
  <Shield size={28} />,
  <BarChart3 size={28} />,
];

const Index = () => {
  const { localePath } = useLocale();
  const t = useTranslation();

  return (
    <Layout>
      <SEO
        title="Employé Incognito — Employé mystère & audit terrain tous secteurs"
        description={t.home.heroDesc}
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          "name": "Employé Incognito",
          "description": t.home.heroDesc,
          "url": "https://employeincognito.com",
          "telephone": "+33689605897",
          "email": "contact" + "@" + "employeincognito.com",
          "logo": "https://employeincognito.com/favicon.ico",
          "image": "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/4248ddce-854c-4119-b691-6d866c5f695c/id-preview-a019ce00--68443070-c2fd-4a86-b1e6-5256a2c18b1c.lovable.app-1772541128193.png",
          "founder": {
            "@type": "Person",
            "name": "Fondateur Employé Incognito",
          },
          "foundingDate": "2024",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "26 rue Bosquet",
            "addressLocality": "Paris",
            "postalCode": "75007",
            "addressRegion": "Île-de-France",
            "addressCountry": "FR",
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": 48.8566,
            "longitude": 2.3522,
          },
          "areaServed": [
            { "@type": "Country", "name": "France" },
            { "@type": "Country", "name": "Suisse" },
            { "@type": "Country", "name": "Luxembourg" },
          ],
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Services d'audit mystère",
            "itemListElement": [
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Audit immersif — Employé Mystère" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Audit terrain multi-secteurs (retail, industrie, services, santé)" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Audit climat social & management" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Audit réseaux & multi-sites" } },
              { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Conseil en excellence opérationnelle" } },
            ],
          },
          "serviceType": ["Audit immersif — Employé Mystère", "Audit terrain multi-secteurs", "Audit climat social & management", "Audit réseaux & multi-sites", "Conseil en excellence opérationnelle"],
          "priceRange": "€€€",
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "09:00",
            "closes": "18:00",
          },
          "sameAs": ["https://www.instagram.com/employeincognito/"],
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "5",
            "reviewCount": "12",
            "bestRating": "5",
          },
        }}
      />
      <section className="relative bg-gradient-hero hero-dark min-h-[92vh] flex items-center overflow-hidden">
        <img
          src={heroImage}
          alt="Employé Incognito — immersion opérationnelle"
          className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 h-[80%] w-auto object-contain opacity-90 z-0 drop-shadow-2xl"
        />
        <div className="absolute inset-0 z-[1] bg-gradient-to-r from-[hsl(0_80%_30%)] via-[hsl(0_78%_32%/0.92)] to-transparent md:w-[75%] pointer-events-none" />
        <div className="ambient-orb-gold w-[400px] h-[400px] top-[10%] right-[5%] opacity-40" />
        <div className="ambient-orb w-[300px] h-[300px] bottom-[15%] left-[10%] opacity-30" />
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/25 to-transparent pointer-events-none" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
            >
              <span className="inline-flex items-center gap-2 text-gold text-xs md:text-sm font-body font-semibold tracking-[0.25em] uppercase mb-8 px-4 py-2 rounded-full border border-gold/30 bg-white/5 backdrop-blur-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
                {t.home.badge}
              </span>
              <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold text-cream leading-[1.08] mb-6 tracking-tight">
                {t.home.heroTitle}{" "}
                <span className="text-shimmer-gold">{t.home.heroHighlight}</span>.
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground font-body leading-relaxed mb-10 max-w-2xl">
                {t.home.heroDesc}
              </p>
              <div className="flex flex-wrap gap-4">
                <Button variant="gold" size="lg" className="shadow-gold hover:-translate-y-0.5 transition-transform duration-300" asChild>
                  <Link to={localePath("/contact")}>
                    {t.home.ctaPrimary}
                    <ArrowRight size={18} />
                  </Link>
                </Button>
                <Button variant="gold-outline" size="lg" className="hover:-translate-y-0.5 transition-transform duration-300" asChild>
                  <Link to={localePath("/services")}>{t.home.ctaSecondary}</Link>
                </Button>
              </div>
              <ul className="mt-10 flex flex-wrap gap-x-6 gap-y-3">
                {t.home.trustItems.map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm font-body text-cream/90">
                    <Check size={16} className="text-gold" /> {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Direction vs terrain */}
      <section className="section-padding bg-background relative overflow-hidden">
        <div className="container mx-auto max-w-6xl relative z-10">
          <SectionHeading subtitle={t.home.gapSubtitle} title={t.home.gapTitle} />
          <div className="grid md:grid-cols-2 gap-0 mt-8 rounded-3xl overflow-hidden border border-gold-subtle shadow-elegant">
            <div className="p-8 md:p-12 bg-card/60">
              <span className="text-xs font-body font-semibold tracking-[0.2em] uppercase text-muted-foreground">{t.home.gapLeftTitle}</span>
              <ul className="mt-6 space-y-5">
                {t.home.gapLeft.map((g) => (
                  <li key={g} className="flex items-start gap-3 font-body text-muted-foreground">
                    <Eye size={18} className="mt-0.5 flex-shrink-0 opacity-60" /> {g}
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-8 md:p-12 bg-gradient-hero hero-dark relative">
              <span className="text-xs font-body font-semibold tracking-[0.2em] uppercase text-gold">{t.home.gapRightTitle}</span>
              <ul className="mt-6 space-y-5">
                {t.home.gapRight.map((g, i) => (
                  <motion.li key={g} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }} className="flex items-start gap-3 font-body text-cream font-medium">
                    <UserCheck size={18} className="mt-0.5 flex-shrink-0 text-gold" /> {g}
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <div className="section-glow-divider" />


      {/* Vidéo immersion (NEW) */}
      <section className="relative bg-navy-deep overflow-hidden">
        <div className="container mx-auto px-4 py-16 md:py-20 relative z-10 max-w-5xl">
          <div className="text-center mb-8">
            <span className="text-gold text-sm font-body font-semibold tracking-[0.2em] uppercase block mb-3">
              {t.home.videoSubtitle}
            </span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-cream leading-tight">
              {t.home.videoTitle}
            </h2>
            <div className="gold-line mt-6" />
            <p className="mt-6 text-muted-foreground max-w-2xl mx-auto font-body">
              {t.home.videoDesc}
            </p>
          </div>
          <div className="relative rounded-2xl overflow-hidden border border-gold-subtle shadow-2xl aspect-video">
            <img
              src={cameleonMetiers}
              alt="Employé Incognito — caméléon multi-métiers : bureau, industrie, retail, logistique, santé, BTP"
              loading="lazy"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background/70 via-background/10 to-transparent pointer-events-none" />
          </div>
        </div>
      </section>

      <div className="section-glow-divider" />

      {/* Nos domaines d'intervention — bento */}
      <section className="section-padding bg-navy relative overflow-hidden">
        <div className="ambient-orb-gold w-[300px] h-[300px] top-[10%] left-[-100px]" />
        <div className="container mx-auto relative z-10">
          <SectionHeading subtitle={t.home.sectorsSubtitle} title={t.home.sectorsTitle} description={t.home.sectorsDesc} />
          <div className="grid grid-cols-1 md:grid-cols-4 auto-rows-[250px] gap-5 mt-8">
            {t.home.sectorsList.map((s, i) => {
              const icons = [<Factory size={22} />, <Store size={22} />, <Truck size={22} />, <Briefcase size={22} />, <HeartPulse size={22} />, <HardHat size={22} />];
              const imgs: (string | null)[] = [sectorIndustrie, sectorRetail, sectorLogistique, sectorServices, null, null];
              const spans = ["md:col-span-2 md:row-span-2", "md:col-span-2", "md:col-span-1", "md:col-span-1", "md:col-span-2", "md:col-span-2"];
              const img = imgs[i];
              return (
                <motion.div key={s.title} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.07 }} className={spans[i]}>
                  <div className="relative h-full rounded-3xl overflow-hidden border border-gold-subtle bg-card/40 group hover:shadow-elegant transition-all duration-500">
                    {img && (
                      <>
                        <img src={img} alt={s.title} loading="lazy" width={1280} height={800} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/85 to-background/5" />
                      </>
                    )}
                    <div className="relative h-full p-6 flex flex-col justify-end">
                      <div className="w-11 h-11 rounded-xl bg-gold/15 border border-gold/30 backdrop-blur-sm flex items-center justify-center text-gold mb-3">{icons[i]}</div>
                      <h3 className={`font-display font-semibold text-cream mb-1 ${i === 0 ? "text-2xl md:text-3xl" : "text-lg"}`}>{s.title}</h3>
                      <p className="text-sm text-muted-foreground font-body leading-relaxed max-w-md">{s.desc}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <div className="section-glow-divider" />

      {/* Votre entreprise est concernée si… (NEW) */}
      <section className="section-padding bg-navy-deep relative overflow-hidden glow-corner-tl">
        <div className="container mx-auto max-w-4xl relative z-10">
          <SectionHeading subtitle={t.home.concerneSubtitle} title={t.home.concerneTitle} />
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-4 mt-4">
            {t.home.concerneItems.map((item, i) => (
              <motion.div key={i} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.06 }} className="flex items-start gap-3 border border-gold-subtle/40 rounded-xl p-4 bg-card/30 hover:border-gold/40 hover:bg-card/50 transition-all duration-300">
                <span className="w-6 h-6 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="text-gold" size={13} />
                </span>
                <p className="text-sm text-cream/90 font-body leading-relaxed">{item}</p>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Button variant="gold" size="lg" asChild>
              <Link to={localePath("/contact")}>{t.home.concerneCta} <ArrowRight size={16} /></Link>
            </Button>
          </div>
        </div>
      </section>

      <div className="section-glow-divider" />

      {/* Exemples d'immersions réelles (NEW) */}
      <section className="section-padding bg-navy relative overflow-hidden">
        <div className="container mx-auto relative z-10">
          <SectionHeading subtitle={t.home.casesSubtitle} title={t.home.casesTitle} />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
            {t.home.cases.map((c, i) => {
              const imgs = [sectorRetail, sectorIndustrie, sectorServices, sectorLogistique];
              return (
                <motion.div key={c.title} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }}>
                  <article className="border border-gold-subtle rounded-2xl overflow-hidden bg-card/40 card-glow h-full flex flex-col hover:-translate-y-1.5 hover:shadow-elegant transition-all duration-500 group">
                    <div className="aspect-[4/3] overflow-hidden">
                      <img src={imgs[i]} alt={`Immersion ${c.sector}`} loading="lazy" width={1280} height={800} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                    <div className="p-6 flex-1 flex flex-col">
                      <span className="text-[10px] font-body font-semibold tracking-[0.2em] uppercase text-gold mb-2">{c.sector}</span>
                      <h3 className="font-display text-lg text-cream font-semibold mb-2">{c.title}</h3>
                      <p className="text-sm text-muted-foreground font-body leading-relaxed">{c.desc}</p>
                    </div>
                  </article>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <div className="section-glow-divider" />

      {/* Présentation */}
      <section className="section-padding bg-navy relative overflow-hidden glow-red">
        <div className="container mx-auto relative z-10">
          <SectionHeading
            subtitle={t.home.whoWeAre}
            title={t.home.whoWeAreTitle}
            description={t.home.whoWeAreDesc}
          />
          <div className="grid md:grid-cols-3 gap-8 mt-8">
            {t.home.values.map((v, i) => (
              <motion.div key={v} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.15 }}>
                <div className="border border-gold-subtle rounded-2xl p-8 text-center bg-secondary/30 hover:bg-secondary/50 hover:-translate-y-1.5 transition-all duration-500 card-glow">
                  <div className="w-14 h-14 mx-auto rounded-full bg-gold/10 border border-gold/20 flex items-center justify-center mb-4">
                    <Award className="text-gold" size={26} />
                  </div>
                  <h3 className="font-display text-lg text-cream font-semibold mb-2">{v}</h3>
                  <p className="text-sm text-muted-foreground font-body">{t.home.valueDescs[i]}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      <div className="section-glow-divider" />

      {/* Services */}
      <section className="section-padding bg-navy-deep relative overflow-hidden">
        <div className="ambient-orb-gold w-[400px] h-[400px] top-[-100px] right-[-100px]" />
        <div className="container mx-auto relative z-10">
          <SectionHeading
            subtitle={t.home.expertiseSubtitle}
            title={t.home.expertiseTitle}
          />
          <div className="grid md:grid-cols-2 gap-6">
            {t.home.services.map((s, i) => (
              <motion.div key={s.title} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }}>
                <div className="border border-gold-subtle rounded-2xl p-8 bg-card/50 card-glow hover:-translate-y-1.5 hover:shadow-elegant transition-all duration-500 group">
                  <div className="w-12 h-12 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center text-gold mb-4 group-hover:scale-110 group-hover:bg-gold/15 transition-all duration-300">{serviceIcons[i]}</div>
                  <h3 className="font-display text-xl text-cream font-semibold mb-3">{s.title}</h3>
                  <p className="text-sm text-muted-foreground font-body leading-relaxed">{s.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button variant="gold-outline" size="lg" asChild>
              <Link to={localePath("/services")}>{t.home.seeAllServices} <ArrowRight size={16} /></Link>
            </Button>
          </div>
        </div>
      </section>
      <div className="section-glow-divider" />

      {/* Chiffres */}
      <section className="section-padding bg-navy relative overflow-hidden">
        <div className="ambient-orb w-[300px] h-[300px] bottom-[-50px] left-[20%]" style={{ animationDelay: '2s' }} />
        <div className="container mx-auto relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {t.home.advantages.map((a, i) => (
              <motion.div key={a.label} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }} className="text-center">
                <span className="font-display text-4xl md:text-5xl font-bold text-shimmer-gold">{a.num}</span>
                <p className="mt-3 text-sm text-muted-foreground font-body">{a.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      <div className="section-glow-divider" />

      {/* Employé Mystère — Nouveau service */}
      <section className="section-padding bg-navy-deep relative overflow-hidden glow-corner-br">
        <div className="container mx-auto max-w-4xl relative z-10">
          <motion.div {...fadeUp}>
            <div className="border border-gold-subtle rounded-3xl p-8 md:p-12 bg-card/30 relative overflow-hidden shadow-elegant">
              <div className="absolute top-4 right-4">
                <span className="text-[10px] font-body font-semibold tracking-[0.2em] uppercase bg-gold/10 text-gold px-3 py-1 rounded-full border border-gold/20">
                  {t.home.immersiveNew}
                </span>
              </div>
              <div className="grid md:grid-cols-2 gap-10 items-center">
                <div>
                  <div className="w-14 h-14 rounded-full bg-gold/10 flex items-center justify-center mb-6">
                    <UserCheck className="text-gold" size={28} />
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl text-cream font-bold mb-4">
                    {t.home.immersiveTitle}
                  </h3>
                  <p className="text-muted-foreground font-body leading-relaxed mb-6">
                    {t.home.immersiveDesc}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button variant="gold" size="default" asChild>
                      <Link to={localePath("/employe-mystere")}>
                        {t.home.immersiveCta}
                        <ArrowRight size={16} />
                      </Link>
                    </Button>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 border border-gold-subtle/50 rounded-lg p-4 bg-secondary/20">
                    <Eye className="text-gold flex-shrink-0" size={20} />
                    <div>
                      <p className="text-sm font-semibold text-cream font-body">{t.home.immersiveClient}</p>
                      <p className="text-xs text-muted-foreground font-body">{t.home.immersiveClientDesc}</p>
                    </div>
                  </div>
                  <div className="w-8 h-px bg-gold/30 mx-auto" />
                  <div className="flex items-center gap-4 border border-gold/30 rounded-lg p-4 bg-gold/5">
                    <UserCheck className="text-gold flex-shrink-0" size={20} />
                    <div>
                      <p className="text-sm font-semibold text-gold font-body">{t.home.immersiveEmploye}</p>
                      <p className="text-xs text-muted-foreground font-body">{t.home.immersiveEmployeDesc}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Secteurs entreprise */}
      <section className="section-padding bg-gradient-hero relative overflow-hidden">
        <div className="container mx-auto max-w-5xl relative z-10 hero-dark">
          <SectionHeading
            subtitle={t.home.enterpriseSubtitle}
            title={t.home.enterpriseTitle}
            description={t.home.enterpriseDesc}
          />
          <div className="grid md:grid-cols-2 gap-6 mt-8">
            {t.home.enterpriseSectors.map((s, i) => {
              const icons = [<Store size={24} />, <Landmark size={24} />, <HeartPulse size={24} />, <Building2 size={24} />];
              return (
                <motion.div key={s.title} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }}>
                  <div className="border border-[hsl(42_80%_50%/0.25)] rounded-2xl p-6 bg-white/10 backdrop-blur-sm hover:bg-white/15 hover:-translate-y-1 transition-all duration-300">
                    <div className="text-gold mb-3">{icons[i]}</div>
                    <h3 className="font-display text-lg text-white font-semibold mb-2">{s.title}</h3>
                    <p className="text-sm text-white/75 font-body leading-relaxed">{s.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
          <div className="text-center mt-10">
            <Button variant="gold" size="lg" asChild>
              <Link to={localePath("/employe-mystere")}>
                {t.home.immersiveCta}
                <ArrowRight size={16} />
              </Link>
            </Button>
          </div>
        </div>
      </section>
      {/* Témoignages */}
      <section className="section-padding bg-navy-deep relative overflow-hidden glow-corner-tl">
        <div className="container mx-auto relative z-10">
          <SectionHeading subtitle={t.home.testimonialsSubtitle} title={t.home.testimonialsTitle} />
          <div className="grid md:grid-cols-3 gap-8">
            {t.home.testimonials.map((te, i) => (
              <motion.div key={i} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.15 }}>
                <div className="border border-gold-subtle rounded-2xl p-8 bg-card/30 h-full flex flex-col card-glow hover:-translate-y-1.5 hover:shadow-elegant transition-all duration-500">
                  <p className="font-accent text-lg italic text-cream/90 leading-relaxed flex-1">"{te.quote}"</p>
                  <div className="mt-6 pt-4 border-t border-gold-subtle">
                    <p className="text-sm font-semibold text-gold font-body">{te.author}</p>
                    <p className="text-xs text-muted-foreground font-body">{te.company}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + Form */}
      <section className="section-padding bg-gradient-hero relative overflow-hidden">
        <div className="container mx-auto max-w-2xl relative z-10">
          <div className="hero-dark">
            <SectionHeading
              subtitle={t.home.ctaSubtitle}
              title={t.home.ctaTitle}
              description={t.home.ctaDesc}
            />
          </div>
          <div className="rounded-xl p-8 md:p-10 bg-white shadow-lg">
            <ContactForm compact />
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
